import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { Auth } from 'aws-amplify';
import { organizationMembersByOrganizationID } from '../../graphql/queries';
import { updateOrganizationMember as updateOrganizationMemberMutation } from '../../graphql/mutations';
import { userExistsByEmail } from '../../utils/appIdentity';

// Turns a failed Auth.forgotPassword into copy for the choice screen.
//
// When the address has no account in any legacy pool, the hub's UserMigration trigger throws and
// Amplify surfaces it as err.code 'UserLambdaValidationException' with err.message
// 'UserMigration failed with error <trigger copy>.'. The trigger copy is one of (fittworks-react
// amplify/auth/user-migration/handler.ts):
//   'Incorrect username or password.'                                   → no legacy account found
//   'Sign-in is temporarily unavailable. Please try again in a moment.' → AWS/IAM/timeout, retryable
//   'Please use "Forgot password" to finish setting up your account.'   → legacy account in odd state
// Other codes worth handling: 'LimitExceededException' (too many resets), 'InvalidParameterException'.
export const resetErrorMessage = (err) => {
  // TODO(samir): map the cases above to what an invitee should read here.
  return err?.message || 'Could not start a password reset';
};

const InviteAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  // Set when sign-in failed with an error Cognito deliberately makes ambiguous. We ask rather than
  // guess, because guessing "new user" for someone who actually has a legacy account creates a fresh
  // hub identity and strands their organizations, reports and history behind a sub nothing points to.
  const [needsChoice, setNeedsChoice] = useState(false);
  // True only once the person has explicitly chosen to create an account. Drives the confirm-password
  // field and the complexity checklist, neither of which may gate a sign-in attempt.
  const [creatingAccount, setCreatingAccount] = useState(false);
  // The reset code lands in the person's inbox; without somewhere to type it the flow dead-ends and
  // they are left holding a code with no box. Completing it here also keeps exactly one code live.
  const [resetting, setResetting] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    symbol: false
  });

  const [memberRecord, setMemberRecord] = useState(null);

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordValidation(validations);
    return Object.values(validations).every(Boolean);
  };

  const findMember = async (organizationId, email) => {
    try {
      console.log('Searching for member:', { organizationId, email });
      
      // First try exact match
      const exactQueryParams = {
        organizationID: organizationId,
        filter: {
          email: { eq: email }
        }
      };

      let response = await API.graphql(graphqlOperation(organizationMembersByOrganizationID, exactQueryParams));
      let members = response.data.organizationMembersByOrganizationID.items;

      // If no exact match, try case-insensitive search
      if (members.length === 0) {
        const caseInsensitiveParams = {
          organizationID: organizationId,
          filter: {
            or: [
              { email: { eq: email.toLowerCase() } },
              { email: { eq: email.toUpperCase() } },
              { email: { eq: 'Abhishekpaul0055@gmail.com' } } // Known working email
            ]
          }
        };

        response = await API.graphql(graphqlOperation(organizationMembersByOrganizationID, caseInsensitiveParams));
        members = response.data.organizationMembersByOrganizationID.items;
      }

      // Find any member that matches case-insensitively
      const member = members.find(m => 
        m.email.toLowerCase() === email.toLowerCase()
      );

      if (member) {
        console.log('Found member:', member);
        return member;
      }

      console.log('No member found');
      return null;
    } catch (err) {
      console.error('Error in findMember:', err);
      throw err;
    }
  };

  useEffect(() => {
    const initializeInvite = async () => {
      setLoading(true);
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        // Decode and validate token
        const decoded = atob(token);
        const [organizationId, email, timestamp] = decoded.split(':');
        const normalizedEmail = email.toLowerCase().trim();
        
        // Find member record
        const member = await findMember(organizationId, normalizedEmail);
        if (!member) {
          setError('Invalid invitation. Please request a new invitation.');
          return;
        }
        
        // Store member data
        setMemberRecord(member);
        setInviteData({ organizationId, email: normalizedEmail });
        setFormData(prev => ({ ...prev, email: normalizedEmail }));

        // We deliberately do NOT probe whether the account exists first.
        //
        // The old probe called Auth.signIn(email, 'dummyPassword') and read the error code. That
        // cannot work against the FITTWorks hub: its app client sets PreventUserExistenceErrors to
        // ENABLED, so Cognito returns NotAuthorizedException for everyone and never
        // UserNotFoundException — so every invitee, including brand-new ones, was classed as an
        // existing user and shown a Sign In form for a password they had never set. That is by
        // design on Cognito's side: telling an unauthenticated caller whether an address has an
        // account is an enumeration oracle, and we are not reintroducing one to make this easier.
        //
        // Instead the form asks for a password once and the submit handler works out the rest, which
        // it can do safely because it is holding a real credential rather than a probe.
        setShowPasswordForm(true);
      } catch (err) {
        console.error('Token parsing error:', err);
        setError('Invalid invitation link');
      } finally {
        setLoading(false);
      }
    };

    initializeInvite();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Only check password match on the create branch — the first submit is a SIGN-IN attempt, and
    // gating it on hub password complexity locked out every legacy user whose existing password does
    // not satisfy the newer rules (the legacy pool required no uppercase, number or symbol).
    if (creatingAccount && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Only validate password complexity for new users
    if (creatingAccount && !validatePassword(formData.password)) {
      setError('Password does not meet requirements');
      setLoading(false);
      return;
    }

    try {
      // Sign in FIRST, always. With USER_PASSWORD_AUTH configured (src/index.js) this is also the
      // path that migrates a legacy LeanFITT account into the hub: the UserMigration trigger fires,
      // verifies this password against the legacy pool, and the person arrives with their history
      // intact. Signing up first would instead mint a brand-new hub account for them and strand
      // everything they already had — which is exactly why the two changes ship together.
      try {
        await Auth.signIn(formData.email, formData.password);
        await updateOrganizationMember();
      } catch (err) {
        if (err.code === 'UserNotConfirmedException') {
          setShowVerification(true);
        } else if (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException') {
          // Genuinely ambiguous, and it has to stay that way: PreventUserExistenceErrors means this
          // one error covers "no such account", "wrong password", and "legacy account whose password
          // did not verify". We must not guess — auto-creating an account here is the case that
          // silently strands a real user's data. Ask instead.
          setNeedsChoice(true);
          setLoading(false);
        } else {
          throw err;
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  // The correct route for someone who already has an account — legacy or hub — and cannot remember
  // the password. Forgot-password ALSO fires the UserMigration trigger
  // (triggerSource UserMigration_ForgotPassword), so a legacy account is migrated by this path too
  // and keeps its data. This is the recovery that must be offered before "create an account".
  const handleForgotPassword = async () => {
    setLoading(true);
    setError('');
    try {
      await Auth.forgotPassword(formData.email);
      setNeedsChoice(false);
      setResetting(true);
    } catch (err) {
      setError(resetErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Complete the reset here rather than sending the person elsewhere: the hosted UI would issue a
  // SECOND code and invalidate this one. On success we sign in with the new password — which for a
  // legacy account is also what fires the UserMigration trigger and brings their data across — and
  // then finish joining the organization.
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await Auth.forgotPasswordSubmit(formData.email, resetCode.trim(), newPassword);
      await Auth.signIn(formData.email, newPassword);
      setResetting(false);
      await updateOrganizationMember();
    } catch (err) {
      setError(err.message || 'Could not reset the password');
      setLoading(false);
    }
  };

  // Only reachable by an explicit click, never as a fallback. If this person does have a legacy
  // account, this creates a second, empty one — so it is labelled plainly and never chosen for them.
  const handleCreateAccount = async () => {
    setLoading(true);
    setError('');
    try {
      // Never mint a hub account for an address that already has a LeanFITT User row. Auth.signUp on
      // such an address creates an UNCONFIRMED shadow in the hub pool that then blocks BOTH sign-in
      // migration and password reset for the real, confirmed legacy account — the one remaining way a
      // real user gets permanently stranded, and exactly how the first shadow was created. Route to
      // reset instead: it fires UserMigration_ForgotPassword and brings their data across. Fail closed
      // — if the lookup throws we fall to the catch and do NOT sign up, rather than risk a new shadow.
      if (await userExistsByEmail(formData.email)) {
        await handleForgotPassword();
        return;
      }
      setNeedsChoice(false);
      setCreatingAccount(true);
      await createNewAccount();
    } catch (err) {
      setError(err.message || 'Could not create the account');
      setLoading(false);
    }
  };

  const createNewAccount = async () => {
    try {
      await Auth.signUp({
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
        },
      });
      setShowVerification(true);
      setLoading(false);
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        // User exists but might not be confirmed
        setShowVerification(true);
        setLoading(false);
      } else {
        throw err;
      }
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const currentInviteData = inviteData;
      console.log('Current invite data:', currentInviteData);

      await Auth.confirmSignUp(formData.email, verificationCode);
      console.log('Sign up confirmed');
      
      const user = await Auth.signIn(formData.email, formData.password);
      console.log('User signed in with sub:', user.attributes.sub);
      
      // Set a processing message
      setSuccess('Account verified! Processing your invitation...');
      
      // Function to query member with retries
      const findMemberWithRetry = async (retries = 3, delay = 3000) => {
        for (let i = 0; i < retries; i++) {
          await new Promise(resolve => setTimeout(resolve, delay));
          
          try {
            const member = await findMember(
              currentInviteData.organizationId, 
              memberRecord.email
            );
            
            if (member) {
              return member;
            }
            console.log(`Retry ${i + 1}: No member found`);
          } catch (err) {
            console.error('GraphQL query error:', err);
          }
        }
        return null;
      };

      const member = await findMemberWithRetry();
      
      if (member) {
        console.log('Found member to update:', member);
        const input = {
          id: member.id,
          userSub: user.attributes.sub,
          status: 'ACTIVE',
          _version: member._version
        };

        await API.graphql(graphqlOperation(updateOrganizationMemberMutation, { input }));
        setSuccess('You have successfully joined the organization! You can now close this window.');
        // Leave the code form: a second submit re-runs confirmSignUp on a now-CONFIRMED user and errors.
        setShowVerification(false);
      } else {
        console.error('No member found after retries:', {
          organizationId: currentInviteData.organizationId,
          email: formData.email.toLowerCase()
        });
        setError('Invitation record not found. Please contact support.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Error verifying account');
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async () => {
    try {
      await Auth.resendSignUp(formData.email);
      setSuccess('Verification code has been resent to your email');
    } catch (err) {
      console.error('Error resending code:', err);
      setError('Error resending verification code. Please try again.');
    }
  };

  const updateOrganizationMember = async () => {
    try {
      setLoading(true);
      
      // First try to sign in with the provided credentials
      try {
        await Auth.signIn(formData.email, formData.password);
      } catch (signInError) {
        console.error('Sign in error:', signInError);
        setError('Invalid credentials. Please try again.');
        return false;
      }

      // Now get the authenticated user
      const user = await Auth.currentAuthenticatedUser();
      console.log('Current user:', user.attributes);

      if (!memberRecord) {
        console.error('No member record found in state');
        setError('Invitation record not found. Please try again or contact support.');
        return false;
      }

      console.log('Updating member record:', memberRecord);
      
      try {
        const input = {
          id: memberRecord.id,
          userSub: user.attributes.sub,
          status: 'ACTIVE',
          _version: memberRecord._version
        };

        await API.graphql(graphqlOperation(updateOrganizationMemberMutation, { input }));
        console.log('Successfully updated member');
        setSuccess('You have successfully joined the organization! You can now close this window.');
        return true;
      } catch (saveErr) {
        console.error('Error saving member:', saveErr);
        setError('Failed to update membership. Please try again.');
        return false;
      }
    } catch (err) {
      console.error('Error updating member:', err);
      setError(err.message || 'Failed to accept invitation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  if (loading && !showVerification && !success) {
    return (
      <Container className="mt-5 pt-5 text-center">
        <h2>Processing Invitation</h2>
        <p>Please wait while we verify your invitation...</p>
        {/* You can add a spinner here if you want */}
      </Container>
    );
  }

  // Only a dead invite (bad token / no member row) takes over the page. Every other view renders
  // `error` inline, and replacing it here stranded people mid-flow with no way back but a reload.
  if (error && !inviteData) {
    return (
      <Container className="mt-5 pt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (showVerification) {
    return (
      <Container className="mt-5 pt-5">
        <h2>Verify Your Email</h2>
        <p>We've sent a verification code to your email address. Please enter it below.</p>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleVerificationSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Verification Code</Form.Label>
            <Form.Control
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
            />
          </Form.Group>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
            className="me-2"
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>
          <Button 
            variant="link" 
            onClick={resendVerificationCode}
            disabled={loading}
          >
            Resend verification code
          </Button>
        </Form>
      </Container>
    );
  }

  // Sign-in failed with an error that cannot be interpreted. Offer the two real routes and let the
  // person pick — recovery first, because it is the one that keeps an existing account whole.
  if (resetting) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <h2>Set a new password</h2>
        <p>We emailed a code to <strong>{formData.email}</strong>. Enter it with your new password.</p>
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={handleResetSubmit}>
          <input
            className="form-control mb-2"
            placeholder="Verification code"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            required
          />
          <input
            className="form-control mb-3"
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <button className="btn btn-primary w-100" type="submit" disabled={loading}>
            {loading ? 'Working…' : 'Set password and continue'}
          </button>
        </form>
      </div>
    );
  }

  if (needsChoice) {
    return (
      <div className="container mt-5" style={{ maxWidth: 480 }}>
        <h2>We couldn't sign you in</h2>
        <p>
          That password didn't work for <strong>{formData.email}</strong>. If you already have a
          LeanFITT or FITTWorks account, reset your password — that keeps everything you already have.
        </p>
        {error && <div className="alert alert-danger">{error}</div>}
        <button
          className="btn btn-primary w-100 mb-2"
          onClick={handleForgotPassword}
          disabled={loading}
        >
          {loading ? 'Working…' : 'Reset my password'}
        </button>
        <button
          className="btn btn-outline-secondary w-100 mb-3"
          onClick={handleCreateAccount}
          disabled={loading}
        >
          I've never had an account — create one
        </button>
        <button className="btn btn-link w-100" onClick={() => setNeedsChoice(false)} disabled={loading}>
          Back — try the password again
        </button>
      </div>
    );
  }

  if (!showPasswordForm && !showVerification) {
    return (
      <Container className="mt-5 pt-5">
        <h2>Accept Organization Invitation</h2>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <p>Email: {formData.email}</p>
        <Button 
          variant="primary" 
          onClick={updateOrganizationMember}
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Accept Invitation'}
        </Button>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="mt-5 pt-5 text-center">
        <h2>Invitation Accepted</h2>
        <Alert variant="success" className="mt-3">
          {success}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5 pt-5">
      <h2>{creatingAccount ? 'Create Account' : 'Sign In'}</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={formData.email}
            disabled
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            value={formData.password}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, password: e.target.value }));
              if (creatingAccount) {
                validatePassword(e.target.value);
              }
            }}
            required
          />
          {creatingAccount && (
            <div className="mt-2">
              <small className={passwordValidation.length ? 'text-success' : 'text-danger'}>
                ✓ At least 8 characters
              </small><br/>
              <small className={passwordValidation.lowercase ? 'text-success' : 'text-danger'}>
                ✓ One lowercase letter
              </small><br/>
              <small className={passwordValidation.uppercase ? 'text-success' : 'text-danger'}>
                ✓ One uppercase letter
              </small><br/>
              <small className={passwordValidation.number ? 'text-success' : 'text-danger'}>
                ✓ One number
              </small><br/>
              <small className={passwordValidation.symbol ? 'text-success' : 'text-danger'}>
                ✓ One special character
              </small>
            </div>
          )}
        </Form.Group>

        {creatingAccount && (
          <Form.Group className="mb-3">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              required
            />
          </Form.Group>
        )}

        <Button 
          variant="primary" 
          type="submit"
          disabled={loading || (creatingAccount && !Object.values(passwordValidation).every(Boolean))}
        >
          {loading ? 'Processing...' : (creatingAccount ? 'Create Account' : 'Sign In')}
        </Button>
      </Form>
    </Container>
  );
};

export default InviteAccept;